import { Text } from "@certn/cascadia";
import { GridItem } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";

import { InvoiceContactEmails } from "./InvoiceContactEmails";
import { PayNowLink } from "./PayNowLink";
import { InvoiceProps } from "../../../../src/schemas-and-types";

interface PaymentProps {
  invoiceData: InvoiceProps["invoiceData"];
}

function getCertnCanadaPaymentFooter(invoiceData: InvoiceProps["invoiceData"]) {
  const isCreditCardClient =
    invoiceData.billing_profile.payment_method === "CREDIT_CARD";
  return (
    <>
      {!isCreditCardClient && (
        <GridItem
          span={invoiceData.stripe_payment_link ? 4 : 6}
          className={styles.noBreak}
        >
          <Text style="global/heading-medium">
            {t("Bank transfer payment")}
          </Text>
          <Text style="global/footer-small">
            {t("Bank: BMO Bank of Montreal")}
            <br />
            {t("Bank Address: {{address}}", {
              address: "595 Burrard St, Vancouver, BC, V7X 1LP",
            })}
            <br />
            {t("Institution Number: {{number}}", { number: "001" })}
            <br />
            {t("Transit/Branch: {{number}}", { number: "00040" })}
            <br />
            {t("CDN$ Account: {{number}}", { number: "1594423" })}
            <br />
            {t("USD$ Account: {{number}}", { number: "4584641" })}
            <br />
            {t("SWIFT Code: {{number}}", { number: "BOMFMCAM2" })}
            <br />
            <InvoiceContactEmails />
          </Text>
        </GridItem>
      )}
      {invoiceData.billing_profile.payment_method === "CHECK" && (
        <GridItem
          span={invoiceData.stripe_payment_link ? 4 : 6}
          className={styles.noBreak}
        >
          <Text style="global/heading-medium">{t("Cheque payment")}</Text>
          <Text style="global/footer-small">
            LockBox
            <br />
            {t("Bank Address: {{address}}", {
              address: "595 Burrard St, Vancouver, BC, V7X 1LP",
            })}
            <br />
            Certn (Canada) Inc.
            <br />
            c/o V7201"
            <br />
            P.O. Box 7100
            <br />
            Station Terminal
            <br />
            Vancouver BC, V6B 4E2
          </Text>
        </GridItem>
      )}
      {invoiceData.stripe_payment_link && (
        <PayNowLink
          stripePaymentLink={invoiceData.stripe_payment_link}
          status={invoiceData.status}
          number={isCreditCardClient ? 12 : 4}
        />
      )}
    </>
  );
}

function getCertnUSAPaymentFooter(invoiceData: InvoiceProps["invoiceData"]) {
  const isCreditCardClient =
    invoiceData.billing_profile.payment_method === "CREDIT_CARD";
  return (
    <>
      {!isCreditCardClient && (
        <GridItem
          span={invoiceData.stripe_payment_link ? 4 : 6}
          className={styles.noBreak}
        >
          <Text style="global/heading-medium">
            {t("Bank transfer payment")}
          </Text>
          <Text style="global/footer-small">
            {t("Bank: BMO Harris Bank")}
            <br />
            {t("USD Routing: {{number}}", { number: "071000288" })}
            <br />
            {t("Account Number: {{number}}", { number: "2095586" })}
            <br />
            {t("SWIFT Code: {{number}}", { number: "HATRUS44" })}
            <br />
            <InvoiceContactEmails />
          </Text>
        </GridItem>
      )}
      {invoiceData.stripe_payment_link && (
        <PayNowLink
          stripePaymentLink={invoiceData.stripe_payment_link}
          status={invoiceData.status}
          number={isCreditCardClient ? 12 : 4}
        />
      )}
    </>
  );
}

function getCredencePaymentFooter(invoiceData: InvoiceProps["invoiceData"]) {
  let iban;
  let accountNumber;

  if (invoiceData.currency === "USD") {
    iban = "GB16BARC20766362703399";
    accountNumber = "62703399";
  } else if (invoiceData.currency === "EUR") {
    iban = "GB58BARC20766366339755";
    accountNumber = "66339755";
  } else {
    iban = "GB27BARC20766343148971";
    accountNumber = "43148971";
  }

  const isCreditCardClient =
    invoiceData.billing_profile.payment_method === "CREDIT_CARD";
  return (
    <>
      {!isCreditCardClient && (
        <GridItem
          span={invoiceData.stripe_payment_link ? 4 : 6}
          className={styles.noBreak}
        >
          <Text style="global/heading-medium">
            {t("Bank transfer payment")}
          </Text>
          <Text style="global/footer-small">
            {t("Bank: Barclays")}
            <br />
            {t("Sort code: {{number}}", { number: "20-76-63" })}
            <br />
            {t("Account number: {{number}}", { number: accountNumber })}
            <br />
            {t("SWIFT Code: {{number}}", { number: "BARCGB22" })}
            <br />
            {t("IBAN: {{number}}", { number: iban })}
            <br />
            <InvoiceContactEmails />
          </Text>
        </GridItem>
      )}
      {invoiceData.stripe_payment_link && (
        <PayNowLink
          stripePaymentLink={invoiceData.stripe_payment_link}
          status={invoiceData.status}
          number={isCreditCardClient ? 12 : 4}
        />
      )}
    </>
  );
}

function getIntercheckPaymentFooter(invoiceData: InvoiceProps["invoiceData"]) {
  const isCreditCardClient =
    invoiceData.billing_profile.payment_method === "CREDIT_CARD";
  return (
    <>
      {!isCreditCardClient && (
        <GridItem
          span={invoiceData.stripe_payment_link ? 4 : 6}
          className={styles.noBreak}
        >
          <Text style="global/heading-medium">
            {t("Bank transfer payment")}
          </Text>
          <Text style="global/footer-small">
            {t("Bank: Commonwealth Bank")}
            <br />
            {t("BSB: {{number}}", { number: "062000" })}
            <br />
            {t("Account number: {{number}}", { number: "20460334" })}
            <br />
            {t("IBAN: {{number}}", { number: "200020460334" })}
            <br />
            {t("SWIFT Code: {{number}}", { number: "CTBAAU2SCLS" })}
            <br />
            {t("ABN: {{number}}", { number: "51 151 778237" })}
            <br />
            <InvoiceContactEmails />
            {t("Payment account name: {{name}}", {
              name: "InterCheck Global Pty Ltd",
            })}
            <br />
          </Text>
        </GridItem>
      )}
      {invoiceData.stripe_payment_link && (
        <PayNowLink
          stripePaymentLink={invoiceData.stripe_payment_link}
          status={invoiceData.status}
          number={isCreditCardClient ? 12 : 4}
        />
      )}
    </>
  );
}

export function PaymentFooter(props: PaymentProps) {
  const { invoiceData } = props;

  // For non client invoices, don't show payment details or pay now links
  // Payment is automatically collected from the applicant's card on file
  if (invoiceData.billing_profile.payer_option !== "CLIENT") {
    return null;
  }

  // Credit card clients pay via the Stripe "Pay Now" link, so EFT bank
  // details are intentionally suppressed (DAT-1402). If the Stripe link is
  // also missing the footer would be empty between two dividers, so bail.
  if (
    invoiceData.billing_profile.payment_method === "CREDIT_CARD" &&
    !invoiceData.stripe_payment_link
  ) {
    return null;
  }

  const subsidiary = invoiceData.subsidiary;
  let paymentFooter;

  if (subsidiary === "INTERCHECK") {
    paymentFooter = getIntercheckPaymentFooter(invoiceData);
  } else if (subsidiary === "CERTN_USA") {
    paymentFooter = getCertnUSAPaymentFooter(invoiceData);
  } else if (subsidiary === "CREDENCE") {
    paymentFooter = getCredencePaymentFooter(invoiceData);
  } else {
    paymentFooter = getCertnCanadaPaymentFooter(invoiceData);
  }

  return paymentFooter;
}

const styles = {
  noBreak: css({
    pageBreakInside: "avoid",
  }),
};
